/** API origin for uploads and media (matches `api()`). */
export function getApiBaseUrl() {
  let baseUrl = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'development' ? 'http://localhost:5164' : '');
  baseUrl = baseUrl.trim().replace(/\/+$/, '');
  if (!baseUrl) return '';
  if (!baseUrl.match(/^https?:\/\//)) baseUrl = `http://${baseUrl}`;
  return baseUrl;
}

/**
 * Secure fetch wrapper: uses REACT_APP_API_URL and attaches Bearer token from Clerk getToken.
 * If REACT_APP_API_URL is empty, uses relative URLs (same origin) for Cloud Run deployment.
 * @param {string} path - API path (e.g. '/api/auth/verify')
 * @param {Object} options - fetch options; getToken (from useAuth()) is used to add Authorization when provided
 * @returns {Promise<Response>}
 */
export async function api(path, options = {}) {
  const { getToken, ...fetchOptions } = options;

  let baseUrl = getApiBaseUrl();

  let url;
  if (!baseUrl) {
    // Use relative URL (same origin) - frontend and API are on same domain
    url = path.startsWith('/') ? path : `/${path}`;
  } else {
    // Use absolute URL (for dev or if explicitly configured)
    if (!baseUrl.match(/^https?:\/\//)) {
      baseUrl = `http://${baseUrl}`;
    }
    url = path.startsWith('http')
      ? path
      : `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  const headers = new Headers(fetchOptions.headers);

  // Let the browser set multipart boundary for FormData uploads
  if (fetchOptions.body instanceof FormData) {
    headers.delete('Content-Type');
  }

  if (getToken && typeof getToken === 'function') {
    try {
      const token = await getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        console.debug('api: getToken() returned null/undefined for', path);
      }
    } catch (err) {
      console.error('api: Error getting token:', err);
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  return response;
}
